import React, { ReactNode, useState, MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Avatar, Menu, MenuItem, Box, IconButton } from '@mui/material';
import { useAuth } from './AuthContext';
import { UserRole } from '../common/enums';
import { useScore } from './ScoreContext';
import ThemeToggle from './common/ThemeToggle';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth(); // Auth context providing user and logout functionality
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { score, refreshScore } = useScore(); // Use the score and refresh function from the context
  const location = useLocation();

  const handleMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const userRole = user?.role;
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isStudentAssignmentRoute = location.pathname.startsWith('/assignment/');
  const canManageAuthoring = userRole === UserRole.Admin || userRole === UserRole.Tutor;
  const isAuthoringAssistantRoute = [
    /^\/course$/,
    /^\/course\/create$/,
    /^\/course\/edit\/\d+$/,
    /^\/course\/\d+$/,
    /^\/course\/\d+\/lesson\/create$/,
    /^\/course\/\d+\/lesson\/edit\/\d+$/,
    /^\/course\/\d+\/lesson\/\d+$/,
    /^\/course\/\d+\/lesson\/\d+\/material\/create$/,
    /^\/course\/\d+\/lesson\/\d+\/exercise\/create$/,
    /^\/course\/\d+\/lesson\/\d+\/exercise\/edit\/\d+$/,
  ].some((pattern) => pattern.test(location.pathname));
  const useFullWidthLayout = isStudentAssignmentRoute || isAuthoringAssistantRoute;

  return (
    <div>
      <AppBar position="static" style={{ backgroundColor: isDevelopment ? '#4caf50' : '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            📚 TutorTrack
            {isDevelopment && (
              <Typography 
                component="span" 
                variant="h5" 
                sx={{ 
                  ml: 2, 
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  padding: '4px 12px',
                  borderRadius: '4px'
                }}
              >
                DEMO
              </Typography>
            )}
          </Typography>
          <ThemeToggle />
          <Button color="inherit" component={Link} to="/">
            🏠 Etusivu
          </Button>          
          {userRole === UserRole.Admin && (
            <>
              <Button color="inherit" component={Link} to="/curriculum">
                🎓 Opinto-ohjelmat
              </Button>
              <Button color="inherit" component={Link} to="/course">
                📖 Kurssit
              </Button>
              <Button color="inherit" component={Link} to="/math-demo">
                🧮 Matematiikka
              </Button>
            </>
          )}
          {canManageAuthoring && userRole !== UserRole.Admin && (
            <>
              <Button color="inherit" component={Link} to="/course">
                📖 Kurssit
              </Button>
              <Button color="inherit" component={Link} to="/math-demo">
                🧮 Matematiikka
              </Button>
            </>
          )}
          {userRole === UserRole.Student && (
            <>
              <Button color="inherit" component={Link} to="/math-demo">
                🧮 Matematiikka
              </Button>
              {user?.email === 'monika.ruotsalainen@gmail.com' && (
              <Button color="inherit" component={Link} to="/fixtext">
                🔧 Korjaus
              </Button>
              )}
              {/* Display the score only for students */}
              {score !== null && (
                <Box display="flex" alignItems="center" ml={2}>
                  <IconButton color="inherit" onClick={refreshScore} title="Päivitä pisteet"> {/* Refresh button */}
                    <span style={{ fontSize: '1.2rem' }}>🪙</span>
                  </IconButton>
                  <Typography variant="subtitle1">
                    {score} pistettä
                  </Typography>
                </Box>
              )}
            </>
          )}
          {user ? (
            <>
              <Avatar alt={user?.name} src={user?.picture} style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={handleMenu} />
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleClose}>
                  <Typography variant="h6" style={{ marginLeft: '10px' }}>
                    {user?.name}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={logout}>🚪 Kirjaudu ulos</MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="inherit" component={Link} to="/login">
              🔑 Kirjaudu
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {useFullWidthLayout ? (
        <Box
          sx={{
            width: '100%',
            pl: { xs: 2, md: 3, lg: 4 },
            pr: isAuthoringAssistantRoute ? 0 : { xs: 2, md: 3, lg: 4 },
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Box>
      ) : (
        <Container>{children}</Container>
      )}
    </div>
  );
};

export default Layout;
